import { useEffect, useState } from "react";
import { crDateTime } from "@/lib/dateFormat";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, BookOpen, MessageSquare, Wallet, Megaphone, Trash2, CheckCheck, Loader2, Mail } from "lucide-react";

const KIND_ICONS = {
  homework_new: BookOpen,
  homework_comment: MessageSquare,
  payment_new: Wallet,
  payment_due_soon: Wallet,
  admin_broadcast: Megaphone,
  system: Bell,
};

export default function Notifications() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const isAdmin = user?.role === "superadmin" || user?.role === "academic_director";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 200 } });
      setItems(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const goToLink = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* */ }
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
    }
    if (!n.link) return;
    if (/^https?:\/\//i.test(n.link)) window.open(n.link, "_blank", "noopener");
    else navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
  };

  const removeOne = async (id) => {
    await api.delete(`/notifications/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <PageHeader icon={Bell} title={t("notifications")} />

      <div className="flex flex-wrap items-center gap-2">
        {items.some((x) => !x.is_read) && (
          <Button variant="outline" size="sm" data-testid="notifs-mark-all-btn" onClick={markAll}>
            <CheckCheck className="h-3.5 w-3.5 mr-1.5" /> {t("mark_all_read")}
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" data-testid="open-broadcast-btn" onClick={() => setBroadcastOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Megaphone className="h-3.5 w-3.5 mr-1.5" /> {t("admin_broadcast_btn")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500 border-slate-200">{t("no_notifications")}</Card>
      ) : (
        <Card className="border-slate-200 divide-y divide-slate-100">
          {items.map((n) => {
            const Icon = KIND_ICONS[n.kind] || Bell;
            return (
              <div key={n.id} data-testid={`notif-row-${n.id}`} className={`p-4 flex items-start gap-3 ${!n.is_read ? "bg-blue-50/30" : ""}`}>
                <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                <button type="button" onClick={() => goToLink(n)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm ${!n.is_read ? "font-semibold text-slate-900" : "text-slate-700"}`}>{n.title}</p>
                    {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />}
                  </div>
                  {n.body && <p className="text-xs text-slate-600 mt-0.5">{n.body}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">{crDateTime(n.created_at)}</p>
                </button>
                <button type="button" data-testid={`notif-delete-${n.id}`} onClick={() => removeOne(n.id)} className="text-slate-300 hover:text-rose-600 shrink-0" title={t("delete")}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </Card>
      )}

      {isAdmin && <BroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} onSent={load} />}
    </div>
  );
}

function BroadcastDialog({ open, onOpenChange, onSent }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", body: "", link: "", target: "role:student", send_email: false });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error(t("title_required")); return; }
    setSending(true);
    try {
      const payload = { title: form.title.trim(), body: form.body || null, link: form.link || null, send_email: !!form.send_email };
      if (form.target.startsWith("role:")) payload.role = form.target.split(":")[1];
      const r = await api.post("/notifications/admin-broadcast", payload);
      const emails = r.data?.emails_sent || 0;
      toast.success(
        emails > 0
          ? `${t("delivered_to")} ${r.data.delivered} · ${emails} ${t("emails_sent")}`
          : `${t("delivered_to")} ${r.data.delivered}`
      );
      onOpenChange(false);
      setForm({ title: "", body: "", link: "", target: "role:student", send_email: false });
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="broadcast-dialog-title">{t("admin_broadcast_btn")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">{t("target_audience")}</Label>
            <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
              <SelectTrigger className="mt-1.5" data-testid="broadcast-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="role:student">{t("all_students")}</SelectItem>
                <SelectItem value="role:teacher">{t("all_teachers")}</SelectItem>
                <SelectItem value="role:academic_director">{t("all_academic_directors")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="bc-title">{t("title")}</Label>
            <Input id="bc-title" data-testid="broadcast-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" maxLength={120} />
          </div>
          <div>
            <Label className="text-xs" htmlFor="bc-body">{t("message")}</Label>
            <Textarea id="bc-body" data-testid="broadcast-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="resize-none mt-1.5" maxLength={500} />
          </div>
          <div>
            <Label className="text-xs" htmlFor="bc-link">{t("link_optional")}</Label>
            <Input id="bc-link" data-testid="broadcast-link-input" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/dashboard/..." className="mt-1.5" />
          </div>
          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Label htmlFor="bc-email" className="text-sm font-medium flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" /> {t("also_send_by_email")}
                </Label>
                <p className="text-[11px] text-slate-500 mt-0.5">{t("also_send_by_email_hint")}</p>
              </div>
              <Switch
                id="bc-email"
                data-testid="broadcast-email-switch"
                checked={!!form.send_email}
                onCheckedChange={(v) => setForm({ ...form, send_email: v })}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button data-testid="broadcast-send-btn" onClick={submit} disabled={sending || !form.title.trim()} className="bg-purple-600 hover:bg-purple-700 text-white">
            {sending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t("send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
